import React, { useState, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';

import { AppProvider, Page, Card, ResourceList, Badge, Thumbnail, Pagination, Spinner, Tabs, Select , TextField, ChoiceList, Banner } from '@shopify/polaris';

import { ResourceTableHeaders, ResourceTableBody } from '@presentational';
import { productsColumns } from '@utils/recordHelpers';

import axiosRails from '@utils/axios';

import FilterTableProvider from '../FilterTableProvider';
import ImportModal from 'components/presentational/shared/ImportFileModal';

const propTypes = {
  products: PropTypes.array.isRequired,
  sellers: PropTypes.array,
  isAdmin: PropTypes.bool.isRequired,
  newURL: PropTypes.string,

  bulkUpdateUploadURL: PropTypes.string,
  bulkActionURL: PropTypes.string.isRequired,
  sellerIsApu: PropTypes.bool.isRequired,
};

const defaultProps = {};

const tabs = [
  {
    id: 'all',
    value: null,
    content: "Todos",
  },
  {
    id: 'synced',
    value: "synced",
    content: I18n.t("enum.status.published"),
  },
  {
    id: 'pending',
    value: "pending",
    content: I18n.t("enum.status.pending"),
  },
  {
    id: 'rejected',
    value: "rejected",
    content: I18n.t("enum.status.rejected"),
  },
  {
    id: 'archived',
    value: 'archived',
    content: 'Archivado'
  }
];

const ProductIndexWrapper = (props) => {
  const [ bulkUpdateFileState, setBulkUpdateFileState ] = useState({
    open: false,
    file: null,
    errors: [],
    importType: 'quantity',
    message: "",
  });
  const [ loading, setLoading ] = useState(false);
  const [ bulkActionResult, setBulkActionResult ] = useState({
    message: "",
    errors: [],
  })

  const submitBulkUpdateFile = async () => {
    setLoading(true);
    
    try {
      let formData = new FormData();
      formData.append("product_import[import_file]", bulkUpdateFileState.file);
      formData.append("product_import[import_type]", bulkUpdateFileState.importType);
      const response = await axiosRails.post(props.bulkUpdateUploadURL, formData);
      const data = response.data;
      setBulkUpdateFileState({ ...bulkUpdateFileState, message: data.message, file: null, })
    } catch (error) {
      if(error.response.data){
        setBulkUpdateFileState({
          ...bulkUpdateFileState,
          errors: error.response.data.errors,
        });
      }
    }
    
    setLoading(false);
  }

  const openBulkUpdateImportModal = () => setBulkUpdateFileState({ ...bulkUpdateFileState, open: true});
  
  const closeBulkUpdateImportModal = () => setBulkUpdateFileState({ ...bulkUpdateFileState, open: false, });

  const handleImportDrop = (_dropFiles, acceptedFiles, _rejectedFiles) => setBulkUpdateFileState({
    ...bulkUpdateFileState, 
    file: acceptedFiles[0],
    errors: []
  });

  const handleImportDelete = () => setBulkUpdateFileState({ ...bulkUpdateFileState, file: null });
  const onChangeImportType = (values, name) => setBulkUpdateFileState({ ...bulkUpdateFileState, importType: values[0] });
  const dismissImportMessage = () => setBulkUpdateFileState({ ...bulkUpdateFileState, message: "", errors: [] });

  const productColumnsResult = useMemo(() => productsColumns(props.isAdmin), [ props.isAdmin ]) ;
  const primaryAction = props.newURL ? { content: 'Agregar Producto', url: props.newURL } : null;
  const bulkUpdateWithFile = { content: I18n.t("actions.bulk_update"), onAction: openBulkUpdateImportModal };
  const dismissBulkActionMessage = () => setBulkActionResult({ message: "", errors: [] })

  const bulkUpdateAPU = async (itemIds, value, paginateCallback) => {
    setLoading(true);
    try {
      const response = await axiosRails.post(props.bulkActionURL, {
        ids: itemIds,
        apu_value: value
      });
      const data = response.data;
      await paginateCallback(); // Refresh results
      setBulkActionResult({ message: data.message, errors: data.errors });
    } catch (error) {
      alert("Ocurrio un error de conexión, por favor intente nuevamente en unos momentos.")
    }
    setLoading(false);
  }

  // Disabled as of latest update since APU is set automatically for APU users and cannot be tangled with
  // const bulkActions = (itemIds, paginateCallback) => [
  //   // { content: "Habilitar APU", onAction: () => bulkUpdateAPU(itemsIds, true, false) },
  //   { content: "Habilitar APU", onAction: () => bulkUpdateAPU(itemIds, true, paginateCallback) },
  //   { content: "Deshabilitar APU", onAction: () => bulkUpdateAPU(itemIds, false, paginateCallback) },
  // ];
  return (
    <FilterTableProvider
      tabs={tabs}
      users={ props.sellers || [] }
      searchName="search_query"

      filterURL="/products"
      itemURL="/products"
      paramsName="product_filter"
      initialFilters={{
        statusTabIndex: 0,
        user_id: props.sellers && props.sellers[0].value,
        search_query: '',
        pendingType: "any",
      }}
      initialRecords={ props.products }
      initialTotalPages={ props.totalPages }
      columnsInfo={ productColumnsResult }
      pageProps={{
        title: I18n.t("activerecord.models.product.other"),
        primaryAction: primaryAction,
        secondaryActions: props.isAdmin || !props.sellerIsApu ? [ bulkUpdateWithFile ] : []
      }}
      loading={ loading }
    >
      { (renderProps) => (
        <div>
          { renderProps.filterState.statusTabIndex == 2 &&
            <ChoiceList
              name="pendingType"
              title={ "" }
              choices={[
                {label: 'Todas', value: 'any'},
                {label: 'Primera publicación', value: 'first_publish'},
                {label: 'Cambios en espera de aprobación', value: 'publish_update'},
              ]}
              selected={ renderProps.filterState.pendingType }
              onChange={ (values, name) => renderProps.updateFilter(values[0], name, "filter") }
            />
          }
          { !!bulkActionResult.message &&
            <Banner title="Resultado" status={ (bulkActionResult.errors.length > 0) ? "warning" : "success"  } onDismiss={ dismissBulkActionMessage }>
              <p>
                { bulkActionResult.message }
              </p>
              { bulkActionResult.errors.length > 0 && bulkActionResult.errors.map(( error, index ) =>
                <p key={index}>{error}</p>
              )}
            </Banner>
          }
          <ImportModal
            open={ bulkUpdateFileState.open }
            loading={ loading }
            importFile={ bulkUpdateFileState.file }
            errors={ bulkUpdateFileState.errors }

            dropZoneLabel=""
            
            onDrop={ handleImportDrop }
            onDeleteFile={ handleImportDelete }
            onUpload={ submitBulkUpdateFile }
            onCancel={ closeBulkUpdateImportModal }

            onDismissMessage={ dismissImportMessage }
            successMessage={ bulkUpdateFileState.message }
          >
            <div className="mb5">
              <p>Archivo csv. Puede actualizar la cantidad de varios productos a la vez.</p>
              <p>La primera fila debe contener la cabecera de 'SKU' y 'Cantidad'</p>
              <p>Se pueden subir un máximo de 50 SKUs por archivo.</p>
              {/* For now only quantity can be updated */}
              {/* <ChoiceList
                name="pendingType"
                title={ "" }
                choices={[
                  {label: `Cantidad`, value: 'quantity'},
                ]}
                selected={ bulkUpdateFileState.importType }
                onChange={ onChangeImportType }
              /> */}
            </div>
          </ImportModal>
        </div>
      )}
    </FilterTableProvider>
  );
}


ProductIndexWrapper.propTypes = propTypes;
ProductIndexWrapper.defaultProps = defaultProps;


export default ProductIndexWrapper;
